import { useState, useEffect, useRef, useCallback } from 'react'
import { WifiOff, RefreshCw } from 'lucide-react'
import WifiPanel from './WifiPanel'

const PING_INTERVAL_MS = 5000
const PING_URL_FALLBACK = '/api/system/ping'

interface Props {
  /** Optionally force-show in dev/preview without an actual outage */
  forceShow?: boolean
}

export default function OfflineOverlay({ forceShow }: Props) {
  const [offline, setOffline] = useState(false)
  const [checking, setChecking] = useState(false)
  const [showWifi, setShowWifi] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isElectron = !!(window as any).electronAPI?.pingServer

  const ping = useCallback(async (): Promise<boolean> => {
    try {
      if (isElectron) {
        const result = await (window as any).electronAPI.pingServer()
        return !!result?.reachable
      }
      // Browser fallback, direct fetch to /api/system/ping
      const res = await fetch(PING_URL_FALLBACK, { signal: AbortSignal.timeout(4000) })
      return res.ok || res.status < 500
    } catch {
      return false
    }
  }, [isElectron])

  const check = useCallback(async () => {
    setChecking(true)
    const ok = await ping()
    setOffline(!ok)
    setChecking(false)
    if (ok) setAttempt(0)
    else setAttempt(a => a + 1)
  }, [ping])

  // Start polling on mount; also listen to browser online/offline events
  useEffect(() => {
    check()
    intervalRef.current = setInterval(check, PING_INTERVAL_MS)
    window.addEventListener('online', check)
    window.addEventListener('offline', check)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      window.removeEventListener('online', check)
      window.removeEventListener('offline', check)
    }
  }, [check])

  const visible = forceShow || offline

  if (!visible) return null

  return (
    <>
      {/* Fullscreen maintenance overlay */}
      <div
        className="fixed inset-0 z-[150] flex flex-col items-center justify-center"
        style={{
          background: '#0b0b0a',
        }}
      >

        {/* Status icon */}
        <div className="relative mb-8">
                    <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-red-500/10 border border-red-500/30">
            <WifiOff className="h-8 w-8 text-red-400" />
          </div>
        </div>

        {/* Text */}
        <h1 className="text-2xl font-bold text-white mb-2">
          Verbinding met server verbroken
        </h1>
        <p className="text-zinc-400 text-base mb-1">
          Proberen verbinding te herstellen...
        </p>
        {attempt > 1 && (
          <p className="text-zinc-600 text-sm">
            Poging {attempt} · controleer WiFi-verbinding
          </p>
        )}

        {/* Spinner */}
        <div className="mt-6 flex items-center gap-2 text-zinc-500 text-sm">
          <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
          {checking ? 'Verbinding controleren...' : `Volgende poging in ${Math.ceil(PING_INTERVAL_MS / 1000)}s`}
        </div>

        {/* WiFi button removed (guests shouldn't see this). Operators use 4-tap gesture top-left. */}

        {/* Retry now */}
        <button
          onClick={() => { setAttempt(0); check() }}
          className="mt-3 text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
        >
          Nu opnieuw proberen
        </button>
      </div>

      {/* WiFi panel on top (legacy fallback, normally triggered by 4-tap) */}
      {showWifi && <WifiPanel onClose={() => { setShowWifi(false); check() }} />}
    </>
  )
}
